import { PropertyCategory, StepId } from '../models/ci-form.model';

export interface CiStep {
  id: StepId;
  /** Pill label — rendered as "01 · Property". */
  label: string;
  /** Card heading, e.g. "01. Property & Asking Price". */
  heading: string;
  description: string;
}

/**
 * The flow is not the same for every property type. A plot has no rental or
 * UDS position to capture; a house is valued as land plus structure. The
 * reference screens confirm this — apartments run five steps, land and houses
 * run four, with different labels.
 */
export const CI_STEP_FLOWS: Record<PropertyCategory, readonly CiStep[]> = {
  'primary-project': [
    {
      id: 'property',
      label: 'Property',
      heading: 'Property & Asking Price',
      description: 'Property information pulled from inventory.',
    },
    {
      id: 'transactions',
      label: 'Benchmarking',
      heading: 'Benchmarking',
      description: 'Choose how the CI benchmark should be calculated for this property.',
    },
    {
      id: 'uds-rental',
      label: 'UDS & Rental',
      heading: 'UDS & Rental',
      description: 'Undivided share of land, rental position and trophy override for this unit.',
    },
    {
      id: 'benchmark',
      label: 'Revised Benchmark',
      heading: 'Revised Benchmark',
      description: 'Adjust the base benchmark for factors comparables cannot capture.',
    },
    {
      id: 'review',
      label: 'Review',
      heading: 'Review & Generate',
      description: 'Verify every input, then generate Circled Intelligence.',
    },
  ],

  resale: [
    {
      id: 'property',
      label: 'Property',
      heading: 'Property & Asking Price',
      description: 'Property information pulled from inventory.',
    },
    {
      id: 'transactions',
      label: 'Benchmarking',
      heading: 'Benchmarking',
      description: 'Choose how the CI benchmark should be calculated for this property.',
    },
    {
      id: 'uds-rental',
      label: 'UDS & Rental',
      heading: 'UDS & Rental',
      description: 'Undivided share of land, rental position and trophy override for this listing.',
    },
    {
      id: 'benchmark',
      label: 'Revised Benchmark',
      heading: 'Revised Benchmark',
      description: 'Adjust the base benchmark for factors comparables cannot capture.',
    },
    {
      id: 'review',
      label: 'Review',
      heading: 'Review & Generate',
      description: 'Verify every input, then generate Circled Intelligence.',
    },
  ],

  'plot-land': [
    {
      id: 'property',
      label: 'Property',
      heading: 'Plot Information',
      description: 'Property information pulled from inventory.',
    },
    {
      id: 'land-value',
      label: 'Land Value',
      heading: 'Land Value',
      description: 'Land rate, asking rate and the attributes that move them.',
    },
    {
      id: 'adjustments',
      label: 'Adjustments',
      heading: 'Land Adjustments',
      description: 'Orientation, road width and premium refine the base land value.',
    },
    {
      id: 'review',
      label: 'Review',
      heading: 'Review & Generate',
      description: 'Verify every input, then generate Circled Intelligence.',
    },
  ],

  'independent-house': [
    {
      id: 'property',
      label: 'Property',
      heading: 'Property Information',
      description: 'Property information pulled from inventory.',
    },
    {
      id: 'land-value',
      label: 'Land Value',
      heading: 'Land Value',
      description: 'The land component, valued independently of the structure.',
    },
    {
      id: 'adjustments',
      label: 'Adjustments',
      heading: 'Land Adjustments',
      description: 'Orientation, road width and premium refine the base land value.',
    },
    {
      id: 'building-value',
      label: 'Building Value',
      heading: 'Building Value',
      description: 'Residual building value from its age and construction class.',
    },
    {
      id: 'review',
      label: 'Review',
      heading: 'Review & Generate',
      description: 'Verify every input, then generate Circled Intelligence.',
    },
  ],
};

export function stepsFor(category: PropertyCategory): readonly CiStep[] {
  return CI_STEP_FLOWS[category];
}

export function stepIndex(category: PropertyCategory, id: StepId): number {
  return stepsFor(category).findIndex((s) => s.id === id) + 1;
}

export function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
